import React, { useState, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatMoney } from '../../utils/formatters';
import { toBRL } from '../../utils/exchange';
import { toast } from '../shared/Toast';
import { v4 as uuidv4 } from 'uuid';
import { parseProventosPdf } from '../../utils/gemini';
import { PortfolioEvolutionChart, DividendsChart } from './InvestCharts';
import { AllocationCard, PassiveIncomeCard } from './InvestGoals';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const TYPE_COLORS = {
  'Tesouro Direto': 'var(--gold)',
  'CDB': '#e24b4a',
  'FII': '#1d9e75',
  'Ações': '#378ADD',
  'LCI/LCA': '#854F0B',
  'Cripto': '#993556',
  'Capitalização': '#7b5ea7',
  'Consórcio': '#0F6E56',
  'ETF': '#533F91',
};

const TYPES = Object.keys(TYPE_COLORS);

// Quantidade de cotas: aceita vírgula OU ponto como decimal ("3,00462" e
// "3.00462" dão o mesmo). Campos type="number" engoliam a vírgula em alguns
// teclados/navegadores pt-BR e "3,00462" virava 300462 — foi assim que um ativo
// real ficou com 200 mil cotas e a cotação automática inflou pra US$ 11 milhões.
function parseQty(raw) {
  let s = String(raw ?? '').trim();
  if (!s) return null;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.'); // 1.234,56 → 1234.56
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

// Checagem de plausibilidade: se o preço por cota implícito (valor ÷ cotas)
// ficar abaixo de 1 centavo, a quantidade quase certamente veio errada
// (vírgula engolida). Pede confirmação em vez de bloquear — casos legítimos
// (cripto fracionada etc.) ainda passam.
function confirmQtyPlausible(quantity, amount, moeda) {
  if (!quantity || !amount || quantity <= 0) return true;
  const perUnit = amount / quantity;
  if (perUnit >= 0.01) return true;
  const fmt = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return window.confirm(
    `⚠️ Confere a quantidade?\n\n${fmt(quantity)} cotas para ${moeda} ${fmt(amount)} dá menos de ${moeda} 0,01 por cota.\n` +
    `Se você digitou "3,00462" e apareceu "300462", a vírgula se perdeu.\n\nSalvar mesmo assim?`
  );
}

function InvestModal({ inv, onClose, onSave }) {
  const { state } = useApp();
  const rate = state.usdRate;

  // Ativo em US$ (usdV2): invested/current/dividendsReceived são guardados em
  // DÓLAR — o usuário digita exatamente o que vê na corretora, sem conversão.
  // A conversão pra R$ acontece só na hora de somar os totais do sistema.
  const [form, setForm] = useState(inv
    ? { ...inv, dividendsReceived: inv.dividendsReceived || '' }
    : { name: '', type: 'Tesouro Direto', institution: '', quantity: '', invested: '', current: '', currency: 'BRL', payAccountId: '', payDate: new Date().toISOString().slice(0, 10) });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isUSD = form.currency === 'USD';

  const save = () => {
    if (!form.name.trim()) { toast.error('Nome do ativo é obrigatório.'); return; }
    if (!form.invested || parseFloat(form.invested) <= 0) { toast.error('Valor investido deve ser maior que zero.'); return; }
    const invested = parseFloat(form.invested) || 0;
    const quantity = parseQty(form.quantity);
    if (!confirmQtyPlausible(quantity, invested, isUSD ? 'US$' : 'R$')) return;
    onSave({
      ...form,
      currency: form.currency || 'BRL',
      usdV2: true,
      quantity,
      invested,
      current: parseFloat(form.current) || invested,
      dividendsReceived: parseFloat(form.dividendsReceived) || 0,
      alertPct: parseFloat(form.alertPct) || 0,
    });
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">
          {inv ? 'Editar investimento' : 'Novo investimento'}
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="form-group">
          <label className="form-label">Nome do ativo *</label>
          <input className="form-control" autoFocus value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Tesouro Selic 2027" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <select className="form-control" value={form.type} onChange={e => set('type', e.target.value)}>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Instituição</label>
            <input className="form-control" list="invest-institutions" value={form.institution} onChange={e => set('institution', e.target.value)} placeholder="Ex: Santander Corretora" />
            <datalist id="invest-institutions">
              {state.accounts.map(a => <option key={a.id} value={a.name} />)}
            </datalist>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Quantidade</label>
            <input className="form-control" type="text" inputMode="decimal" value={form.quantity} onChange={e => set('quantity', e.target.value)} placeholder="Ex: 200 ou 3,00462" />
          </div>
          <div className="form-group">
            <label className="form-label">Moeda</label>
            <select className="form-control" value={form.currency || 'BRL'} onChange={e => set('currency', e.target.value)}>
              <option value="BRL">R$ Real</option>
              <option value="USD">US$ Dólar</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Valor investido ({isUSD ? 'US$' : 'R$'}) *</label>
            <input className="form-control" type="number" step="0.01" min="0" value={form.invested} onChange={e => set('invested', e.target.value)} placeholder="0,00" />
          </div>
          <div className="form-group">
            <label className="form-label">Valor atual ({isUSD ? 'US$' : 'R$'})</label>
            <input className="form-control" type="number" step="0.01" min="0" value={form.current} onChange={e => set('current', e.target.value)} placeholder="0,00" />
          </div>
        </div>
        {isUSD && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -8, marginBottom: 16 }}>
            {!!parseFloat(form.invested) && (
              <div style={{ color: 'var(--gold)', fontWeight: 600, marginBottom: 4 }}>
                <i className="fas fa-calculator" style={{ marginRight: 6 }}></i>
                ≈ {formatCurrency((parseFloat(form.invested) || 0) * rate)} pelo câmbio do dia (R$ {rate.toFixed(2).replace('.', ',')}) — só informativo, o ativo fica guardado em US$
              </div>
            )}
            <i className="fas fa-circle-info" style={{ marginRight: 6 }}></i>
            Digite em dólar, igual aparece na corretora. Se preencher a Quantidade e o nome terminar com o ticker americano (ex: "... - SGOV"), a cotação atualiza sozinha.
          </div>
        )}

        {/* Alerta de variação diária — checado a cada 30min junto com a cotação */}
        <div className="form-group">
          <label className="form-label">Alerta de variação diária <span style={{ color: 'var(--muted)', fontWeight: 400 }}>via push/WhatsApp</span></label>
          <select className="form-control" value={form.alertPct || 0} onChange={e => set('alertPct', parseFloat(e.target.value))}>
            <option value={0}>Desligado</option>
            <option value={3}>Avisar se variar 3% ou mais no dia</option>
            <option value={5}>Avisar se variar 5% ou mais no dia</option>
            <option value={10}>Avisar se variar 10% ou mais no dia</option>
          </select>
          {(form.alertPct || 0) > 0 && !form.quantity && (
            <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 4 }}>
              <i className="fas fa-triangle-exclamation" style={{ marginRight: 4 }}></i>
              O alerta depende da cotação automática — preencha a Quantidade e termine o nome com o ticker (ex: "... - SGOV").
            </div>
          )}
        </div>

        {inv ? (
          // Edição: permite acertar o total de dividendos já recebidos (conciliação com a corretora)
          <div className="form-group">
            <label className="form-label">Dividendos já recebidos ({isUSD ? 'US$' : 'R$'}) <span style={{ color: 'var(--muted)', fontWeight: 400 }}>opcional</span></label>
            <input className="form-control" type="number" step="0.01" min="0" value={form.dividendsReceived} onChange={e => set('dividendsReceived', e.target.value)} placeholder="0,00" />
          </div>
        ) : (
          // Novo ativo: registrar a 1ª compra debita a conta e aparece no extrato do ativo
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Conta que pagou <span style={{ color: 'var(--muted)', fontWeight: 400 }}>opcional</span></label>
              <select className="form-control" value={form.payAccountId} onChange={e => set('payAccountId', e.target.value)}>
                <option value="">Não lançar a compra</option>
                {state.accounts.filter(a => a.active).map(a => <option key={a.id} value={a.id}>{a.name}{a.currency === 'USD' ? ' (US$)' : ''}</option>)}
              </select>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Debita o valor investido da conta e registra a 1ª compra no extrato do ativo.</div>
            </div>
            {form.payAccountId && (
              <div className="form-group">
                <label className="form-label">Data da compra</label>
                <input className="form-control" type="date" value={form.payDate} onChange={e => set('payDate', e.target.value)} />
              </div>
            )}
          </div>
        )}

        <button className="btn btn-primary btn-full" onClick={save} style={{ padding: 12 }}>
          <i className="fas fa-check"></i> Salvar
        </button>
      </div>
    </div>
  );
}

function SellModal({ inv, onClose, onConfirm }) {
  const { state } = useApp();
  const isPartial = !!inv.quantity;
  const [qty, setQty] = useState(inv.quantity || '');
  const [amount, setAmount] = useState(inv.current);
  const [accountId, setAccountId] = useState(state.accounts.find(a => a.active)?.id || '');
  const accIsUSD = state.accounts.find(a => a.id === accountId)?.currency === 'USD';

  const confirm = () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Informe o valor recebido.'); return; }
    if (!accountId) { toast.error('Escolha a conta que recebeu o valor.'); return; }
    const soldQty = isPartial ? parseQty(qty) || 0 : null;
    if (isPartial && (soldQty <= 0 || soldQty > inv.quantity)) { toast.error('Quantidade vendida inválida.'); return; }
    onConfirm({ amount: parseFloat(amount), accountId, soldQty });
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">
          Vender / Resgatar
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>{inv.name}</div>

        {isPartial && (
          <div className="form-group">
            <label className="form-label">Quantidade vendida (de {inv.quantity})</label>
            <input className="form-control" type="text" inputMode="decimal" value={qty} onChange={e => setQty(e.target.value)} placeholder="Ex: 10 ou 0,5" />
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Valor recebido ({accIsUSD ? 'US$' : 'R$'}) *</label>
          <input className="form-control" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0,00" autoFocus />
          {accIsUSD && !!parseFloat(amount) && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>≈ {formatCurrency(parseFloat(amount) * state.usdRate)} pelo câmbio do dia</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Conta que recebeu *</label>
          <select className="form-control" value={accountId} onChange={e => setAccountId(e.target.value)}>
            <option value="">Selecione</option>
            {state.accounts.filter(a => a.active).map(a => <option key={a.id} value={a.id}>{a.name}{a.currency === 'USD' ? ' (US$)' : ''}</option>)}
          </select>
        </div>

        <button className="btn btn-primary btn-full" onClick={confirm} style={{ padding: 12 }}>
          <i className="fas fa-check"></i> Confirmar
        </button>
      </div>
    </div>
  );
}

function BuyMoreModal({ inv, onClose, onConfirm }) {
  const { state } = useApp();
  const hasQty = !!inv.quantity;
  const [qty, setQty] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [accountId, setAccountId] = useState(state.accounts.find(a => a.active)?.id || '');
  const accIsUSD = state.accounts.find(a => a.id === accountId)?.currency === 'USD';
  const invIsUSD = inv.currency === 'USD';
  const invMoeda = invIsUSD ? 'US$' : 'R$';

  const currentAvgPrice = hasQty && inv.quantity > 0 ? inv.invested / inv.quantity : 0;
  const boughtQty = parseQty(qty) || 0;
  const boughtAmount = parseFloat(amount) || 0;
  // Campos do investimento ficam na moeda do ATIVO; se a conta pagadora usa
  // outra moeda, converte pelo câmbio do dia
  const boughtAmountInv = accIsUSD === invIsUSD
    ? boughtAmount
    : (invIsUSD ? boughtAmount / state.usdRate : boughtAmount * state.usdRate);
  const newQuantity = (inv.quantity || 0) + boughtQty;
  const newInvested = inv.invested + boughtAmountInv;
  const newAvgPrice = hasQty && newQuantity > 0 ? newInvested / newQuantity : 0;

  const confirm = () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Informe o valor pago.'); return; }
    if (hasQty && boughtQty <= 0) { toast.error('Informe a quantidade comprada.'); return; }
    if (!accountId) { toast.error('Escolha a conta que pagou.'); return; }
    if (hasQty && !confirmQtyPlausible(boughtQty, boughtAmountInv, invMoeda)) return;
    onConfirm({ qty: hasQty ? boughtQty : null, amount: boughtAmount, accountId, date });
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">
          {hasQty ? 'Comprar mais' : 'Aportar mais'}
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
          {inv.name}{hasQty && ` — hoje ${inv.quantity} cotas, preço médio ${invMoeda} ${currentAvgPrice.toFixed(2)}`}
        </div>

        {hasQty && (
          <div className="form-group">
            <label className="form-label">Quantidade comprada *</label>
            <input className="form-control" type="text" inputMode="decimal" value={qty} onChange={e => setQty(e.target.value)} placeholder="Ex: 2 ou 0,5" autoFocus />
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Valor pago ({accIsUSD ? 'US$' : 'R$'}) *</label>
            <input className="form-control" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0,00" autoFocus={!hasQty} />
            {accIsUSD !== invIsUSD && boughtAmount > 0 && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>≈ {invMoeda} {boughtAmountInv.toFixed(2).replace('.', ',')} no ativo, pelo câmbio do dia</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Data</label>
            <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Conta que pagou *</label>
          <select className="form-control" value={accountId} onChange={e => setAccountId(e.target.value)}>
            <option value="">Selecione</option>
            {state.accounts.filter(a => a.active).map(a => <option key={a.id} value={a.id}>{a.name}{a.currency === 'USD' ? ' (US$)' : ''}</option>)}
          </select>
        </div>

        {hasQty && boughtQty > 0 && boughtAmount > 0 && (
          <div style={{ fontSize: 12, color: 'var(--gold)', background: 'rgba(var(--gold-rgb),0.08)', border: '1px solid rgba(var(--gold-rgb),0.25)', borderRadius: 8, padding: '8px 12px', marginBottom: 16 }}>
            <i className="fas fa-calculator" style={{ marginRight: 6 }}></i>
            Novo total: {Math.round(newQuantity * 100000) / 100000} cotas — preço médio passa pra {invMoeda} {newAvgPrice.toFixed(2)}
          </div>
        )}

        <button className="btn btn-primary btn-full" onClick={confirm} style={{ padding: 12 }}>
          <i className="fas fa-check"></i> Confirmar
        </button>
      </div>
    </div>
  );
}

function DividendModal({ inv, onClose, onConfirm }) {
  const { state } = useApp();
  const [tipo, setTipo] = useState('Dividendo');
  const [amount, setAmount] = useState('');
  const [tax, setTax] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [accountId, setAccountId] = useState(state.accounts.find(a => a.active)?.id || '');
  const accIsUSD = state.accounts.find(a => a.id === accountId)?.currency === 'USD';
  const moeda = accIsUSD ? 'US$' : 'R$';
  const liquido = (parseFloat(amount) || 0) - (parseFloat(tax) || 0);

  const confirm = () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Informe o valor recebido.'); return; }
    if (!accountId) { toast.error('Escolha a conta que recebeu o valor.'); return; }
    if (parseFloat(tax) < 0 || (parseFloat(tax) || 0) >= parseFloat(amount)) {
      if (tax !== '' && (parseFloat(tax) || 0) >= parseFloat(amount)) { toast.error('O imposto não pode ser maior ou igual ao valor bruto.'); return; }
    }
    onConfirm({ amount: parseFloat(amount), tax: parseFloat(tax) || 0, accountId, date, tipo });
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">
          Dividendo / JCP recebido
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>{inv.name}</div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <select className="form-control" value={tipo} onChange={e => setTipo(e.target.value)}>
              <option value="Dividendo">Dividendo</option>
              <option value="JCP">JCP (Juros sobre Capital Próprio)</option>
              <option value="Rendimento">Rendimento (FII)</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Data</label>
            <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Valor bruto ({moeda}) *</label>
            <input className="form-control" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0,00" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Imposto retido ({moeda}) <span style={{ color: 'var(--muted)', fontWeight: 400 }}>opcional</span></label>
            <input className="form-control" type="number" step="0.01" min="0" value={tax} onChange={e => setTax(e.target.value)} placeholder="0,00" />
          </div>
        </div>
        {!!parseFloat(amount) && (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: -8, marginBottom: 14 }}>
            {parseFloat(tax) > 0 && <>Líquido na conta: <strong style={{ color: 'var(--gold)' }}>{moeda} {liquido.toFixed(2).replace('.', ',')}</strong> (é o que soma nos dividendos do ativo). </>}
            {accIsUSD && <>≈ {formatCurrency((parseFloat(tax) > 0 ? liquido : parseFloat(amount)) * state.usdRate)} pelo câmbio do dia.</>}
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Conta que recebeu *</label>
          <select className="form-control" value={accountId} onChange={e => setAccountId(e.target.value)}>
            <option value="">Selecione</option>
            {state.accounts.filter(a => a.active).map(a => <option key={a.id} value={a.id}>{a.name}{a.currency === 'USD' ? ' (US$)' : ''}</option>)}
          </select>
        </div>

        <button className="btn btn-primary btn-full" onClick={confirm} style={{ padding: 12 }}>
          <i className="fas fa-check"></i> Confirmar
        </button>
      </div>
    </div>
  );
}

// Modal de confirmação de um provento previsto (dividendo/JCP anunciado, ainda
// não pago). Reaproveitado tanto na tela de Investimentos quanto no Calendário.
export function ConfirmProventoModal({ investment, provento, onClose }) {
  const { state, dispatch } = useApp();
  const [accountId, setAccountId] = useState(state.accounts.find(a => a.active)?.id || '');

  const removeProvento = (inv) => ({
    ...inv,
    proventosPrevistos: (inv.proventosPrevistos || []).filter(p => p.id !== provento.id),
  });

  const confirmReceived = () => {
    if (!accountId) { toast.error('Escolha a conta que recebeu.'); return; }
    dispatch({
      type: 'UPDATE_INVESTMENT',
      payload: {
        ...removeProvento(investment),
        dividendsReceived: Math.round(((investment.dividendsReceived || 0) + provento.valorLiquido) * 100) / 100,
      },
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: uuidv4(),
        description: `${provento.tipo} — ${investment.name}`,
        value: provento.valorLiquido,
        type: 'income',
        accountId,
        date: new Date().toISOString().slice(0, 10),
        status: 'paid',
        categoryId: 'inv',
        recurrent: false,
        notes: `${provento.tipo} confirmado a partir de um provento previsto importado`,
      },
    });
    toast.success('Provento confirmado!');
    onClose();
  };

  const ignore = () => {
    dispatch({ type: 'UPDATE_INVESTMENT', payload: removeProvento(investment) });
    toast.success('Provento previsto removido.');
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-handle"></div>
        <div className="modal-title">
          Provento previsto
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>{investment.name} — {provento.tipo}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--green)', marginBottom: 4 }}>{formatCurrency(provento.valorLiquido)}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>Previsto pra {new Date(provento.data + 'T00:00:00').toLocaleDateString('pt-BR')}</div>

        <div className="form-group">
          <label className="form-label">Conta que recebeu *</label>
          <select className="form-control" value={accountId} onChange={e => setAccountId(e.target.value)}>
            <option value="">Selecione</option>
            {state.accounts.filter(a => a.active).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <button className="btn btn-primary btn-full" onClick={confirmReceived} style={{ padding: 12, marginBottom: 8 }}>
          <i className="fas fa-check"></i> Já recebi — confirmar
        </button>
        <button className="btn btn-ghost btn-full" onClick={ignore} style={{ padding: 10 }}>
          Remover (não recebi / valor mudou)
        </button>
      </div>
    </div>
  );
}

// Extrato de todas as movimentações registradas para um ativo.
function InvestDetailModal({ inv, onClose, onEdit, onBuyMore, onSell, onDividend, onDelete }) {
  const { state } = useApp();
  const color = TYPE_COLORS[inv.type] || '#888';
  const gain = inv.current - inv.invested;
  const gainPct = inv.invested > 0 ? (gain / inv.invested * 100).toFixed(2) : '0.00';

  // Transações relacionadas: descrição termina com "— {nome do ativo}"
  const suffix = `— ${inv.name}`;
  const relatedTxs = (state.transactions || [])
    .filter(t => t.description && t.description.endsWith(suffix))
    .sort((a, b) => b.date.localeCompare(a.date));

  const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('pt-BR');

  const txMeta = (t) => {
    if (t.description.startsWith('Venda') || t.description.startsWith('Resgate'))
      return { icon: 'fa-arrow-right-from-bracket', color: 'var(--green)', label: 'Venda / Resgate' };
    if (t.description.startsWith('Imposto retido'))
      return { icon: 'fa-percent', color: 'var(--red)', label: 'Imposto retido' };
    if (t.type === 'income')
      return { icon: 'fa-coins', color: 'var(--gold)', label: t.description.split(' — ')[0] || 'Dividendo' };
    return { icon: 'fa-arrow-right-to-bracket', color: 'var(--blue)', label: 'Compra / Aporte' };
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-handle"></div>

        {/* Cabeçalho */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>
            {inv.name.charAt(0)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.name}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{inv.type}{inv.institution ? ` · ${inv.institution}` : ''}</div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Métricas resumidas */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Investido</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{formatMoney(inv.invested, inv.currency)}</div>
          </div>
          <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Valor atual</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--blue)' }}>{formatMoney(inv.current, inv.currency)}</div>
          </div>
          <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Rendimento</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: gain >= 0 ? 'var(--green)' : 'var(--red)' }}>
              {gain >= 0 ? '+' : ''}{formatMoney(gain, inv.currency)} ({gain >= 0 ? '+' : ''}{gainPct}%)
            </div>
          </div>
          {inv.quantity > 0 ? (
            <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>Cotas · Preço médio</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{inv.quantity} · {inv.currency === 'USD' ? 'US$' : 'R$'} {(inv.invested / inv.quantity).toFixed(2)}</div>
            </div>
          ) : null}
          {!!inv.dividendsReceived && (
            <div style={{ background: 'rgba(var(--gold-rgb),0.08)', borderRadius: 10, padding: '10px 14px', gridColumn: inv.quantity > 0 ? 'auto' : '1 / -1' }}>
              <div style={{ fontSize: 11, color: 'var(--gold)', marginBottom: 2 }}>Dividendos / JCP recebidos (líquido)</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--gold)' }}>{formatMoney(inv.dividendsReceived, inv.currency)}</div>
            </div>
          )}
        </div>

        {/* Extrato */}
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text2)' }}>
          <i className="fas fa-list-ul" style={{ marginRight: 6 }}></i>
          Extrato · {relatedTxs.length} {relatedTxs.length === 1 ? 'movimentação' : 'movimentações'}
        </div>

        {relatedTxs.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: '20px 0', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 16 }}>
            Nenhuma movimentação registrada ainda.<br />
            <span style={{ fontSize: 12 }}>Use "Comprar mais" para registrar aportes.</span>
          </div>
        ) : (
          <div style={{ maxHeight: 230, overflowY: 'auto', marginBottom: 16, borderRadius: 8, border: '1px solid var(--border)' }}>
            {relatedTxs.map((t, idx) => {
              const m = txMeta(t);
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: idx < relatedTxs.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: `${m.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className={`fas ${m.icon}`} style={{ color: m.color, fontSize: 13 }}></i>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{formatDate(t.date)}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.type === 'expense' ? 'var(--red)' : 'var(--green)', textAlign: 'right', flexShrink: 0 }}>
                    {t.type === 'expense' ? '−' : '+'}{formatMoney(t.value, t.currency)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Botões de ação */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <button className="btn btn-primary" onClick={() => { onClose(); onBuyMore(inv); }}>
            <i className="fas fa-cart-plus"></i> {inv.quantity ? 'Comprar mais' : 'Aportar mais'}
          </button>
          <button className="btn btn-ghost" onClick={() => { onClose(); onDividend(inv); }}>
            <i className="fas fa-coins"></i> Dividendo
          </button>
          <button className="btn btn-ghost" onClick={() => { onClose(); onSell(inv); }}>
            <i className="fas fa-sack-dollar"></i> Vender / Resgatar
          </button>
          <button className="btn btn-ghost" onClick={() => { onClose(); onEdit(inv); }}>
            <i className="fas fa-pen"></i> Editar dados
          </button>
        </div>
        <button className="btn btn-ghost btn-full" style={{ color: 'var(--red)', borderColor: 'transparent' }} onClick={() => { onClose(); onDelete(inv.id); }}>
          <i className="fas fa-trash"></i> Excluir investimento
        </button>
      </div>
    </div>
  );
}

// Prévia do que vai ser importado do PDF de proventos previstos da corretora.
// Só entram os que batem com um ativo tipo FII já cadastrado na carteira — Ações
// já são cobertas automaticamente pela cotação (brapi.dev), então ficam de fora
// pra não duplicar.
function ImportProventosModal({ rows, investments, onClose, onImport }) {
  const matched = rows.map(r => {
    const inv = investments.find(i => i.name.trim().toUpperCase() === (r.ticker || '').trim().toUpperCase());
    const isFii = inv?.type === 'FII';
    const dup = isFii && (inv.proventosPrevistos || []).some(p => p.data === r.data && Math.abs(p.valorLiquido - r.valorLiquido) < 0.01);
    return { ...r, inv, isFii, dup };
  });
  const importable = matched.filter(m => m.isFii && !m.dup);
  const skipped = matched.filter(m => !m.isFii);
  const dupes = matched.filter(m => m.isFii && m.dup);
  const [checked, setChecked] = useState(() => new Set(importable.map((_, i) => i)));

  const toggle = (i) => setChecked(s => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });

  const confirm = () => {
    const selected = importable.filter((_, i) => checked.has(i));
    if (!selected.length) { toast.error('Nenhum item selecionado.'); return; }
    onImport(selected);
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-handle"></div>
        <div className="modal-title">
          Importar Proventos Previstos
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {importable.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
            Nenhum provento de FII novo encontrado neste extrato.
            {skipped.length > 0 && ` ${skipped.length} item(ns) de Ações/outros foram ignorados (já são automáticos).`}
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 12 }}>
              {importable.length} provento(s) de FII encontrados — desmarque o que não quiser importar.
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 12 }}>
              {importable.map((r, i) => (
                <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(i)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{r.inv.name} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>· {r.tipo}</span></div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{new Date(r.data + 'T00:00:00').toLocaleDateString('pt-BR')}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>{formatCurrency(r.valorLiquido)}</div>
                </label>
              ))}
            </div>
          </>
        )}

        {(skipped.length > 0 || dupes.length > 0) && (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16 }}>
            {skipped.length > 0 && <div>{skipped.length} item(ns) de Ações/não encontrados na carteira — ignorados (já cobertos automaticamente).</div>}
            {dupes.length > 0 && <div>{dupes.length} já importado(s) antes — ignorados.</div>}
          </div>
        )}

        {importable.length > 0 && (
          <button className="btn btn-primary btn-full" onClick={confirm} style={{ padding: 12 }}>
            <i className="fas fa-check"></i> Importar selecionados
          </button>
        )}
      </div>
    </div>
  );
}

export default function Investments() {
  const { state, dispatch } = useApp();
  const investments = state.investments || [];
  const [modal, setModal] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [sellInv, setSellInv] = useState(null);
  const [buyMoreInv, setBuyMoreInv] = useState(null);
  const [dividendInv, setDividendInv] = useState(null);
  const [confirmProvento, setConfirmProvento] = useState(null); // { investment, provento }
  const [detailInv, setDetailInv] = useState(null);
  const [activeType, setActiveType] = useState('Todos');
  const [importRows, setImportRows] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const typesPresent = TYPES.filter(t => investments.some(i => i.type === t));
  const tabs = ['Todos', ...typesPresent];
  const visibleInvestments = activeType === 'Todos' ? investments : investments.filter(i => i.type === activeType);

  // Totais gerais sempre em R$ — ativos em US$ entram convertidos pelo câmbio do dia
  const totalInvested = investments.reduce((s, i) => s + toBRL(i.invested, i.currency, state.usdRate), 0);
  const totalCurrent = investments.reduce((s, i) => s + toBRL(i.current, i.currency, state.usdRate), 0);
  const totalGain = totalCurrent - totalInvested;
  const gainPct = totalInvested > 0 ? (totalGain / totalInvested * 100).toFixed(2) : '0.00';

  const byType = investments.reduce((map, inv) => {
    if (!map[inv.type]) map[inv.type] = { invested: 0, current: 0 };
    map[inv.type].invested += toBRL(inv.invested, inv.currency, state.usdRate);
    map[inv.type].current += toBRL(inv.current, inv.currency, state.usdRate);
    return map;
  }, {});

  // Todos os proventos previstos de todos os ativos, juntos numa lista só,
  // ordenados por data — é a "aba de proventos" que o Eder pediu pra achar fácil.
  const allProventos = investments
    .flatMap(inv => (inv.proventosPrevistos || []).map(p => ({ ...p, investment: inv })))
    .sort((a, b) => a.data.localeCompare(b.data));
  const totalProventosPrevistos = allProventos.reduce((s, p) => s + p.valorLiquido, 0);

  const handleSave = (invForm) => {
    const { payAccountId, payDate, ...inv } = invForm;
    if (editInv) {
      dispatch({ type: 'UPDATE_INVESTMENT', payload: { ...inv, id: editInv.id } });
      return;
    }
    dispatch({ type: 'ADD_INVESTMENT', payload: inv });
    // 1ª compra: debita a conta escolhida e entra no extrato do ativo
    // (sem isso o saldo da conta fica inflado e o extrato começa "do nada")
    if (payAccountId) {
      const accCur = txCurrencyOf(payAccountId);
      const invCur = inv.currency === 'USD' ? 'USD' : 'BRL';
      const txValue = accCur === invCur
        ? inv.invested
        : Math.round((accCur === 'USD' ? inv.invested / state.usdRate : inv.invested * state.usdRate) * 100) / 100;
      dispatch({
        type: 'ADD_TRANSACTION',
        payload: {
          id: uuidv4(),
          description: `Compra — ${inv.name}`,
          value: txValue,
          currency: accCur,
          type: 'expense',
          accountId: payAccountId,
          date: payDate || new Date().toISOString().slice(0, 10),
          status: 'paid',
          categoryId: 'other-ex',
          recurrent: false,
          notes: 'Lançado automaticamente ao cadastrar o investimento (1ª compra)',
        },
      });
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Excluir este investimento?')) {
      dispatch({ type: 'DELETE_INVESTMENT', payload: id });
    }
  };

  // A transação sai na moeda da CONTA (debita o saldo certo); os campos do
  // investimento ficam na moeda do ATIVO (US$ quando currency==='USD', usdV2).
  // Quando as moedas diferem, converte pelo câmbio do dia.
  const txCurrencyOf = (accountId) => state.accounts.find(a => a.id === accountId)?.currency === 'USD' ? 'USD' : 'BRL';
  const amountInInvCurrency = (amount, accountId, inv) => {
    const accCur = txCurrencyOf(accountId);
    const invCur = inv.currency === 'USD' ? 'USD' : 'BRL';
    if (accCur === invCur) return amount;
    return Math.round((invCur === 'USD' ? amount / state.usdRate : amount * state.usdRate) * 100) / 100;
  };
  // Cotas fracionadas com 5 casas decimais (ex: 3,79206 — igual à corretora)
  const round5 = (v) => Math.round(v * 100000) / 100000;

  const handleBuyMore = ({ qty, amount, accountId, date }) => {
    const inv = buyMoreInv;
    const amountInv = amountInInvCurrency(amount, accountId, inv);
    dispatch({
      type: 'UPDATE_INVESTMENT',
      payload: {
        ...inv,
        quantity: qty != null ? round5((inv.quantity || 0) + qty) : inv.quantity,
        invested: Math.round((inv.invested + amountInv) * 100) / 100,
        current: Math.round((inv.current + amountInv) * 100) / 100,
      },
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: uuidv4(),
        description: `Compra — ${inv.name}`,
        value: amount,
        currency: txCurrencyOf(accountId),
        type: 'expense',
        accountId,
        date,
        status: 'paid',
        categoryId: 'other-ex',
        recurrent: false,
        notes: 'Lançado automaticamente ao comprar mais do investimento',
      },
    });
    toast.success('Compra registrada — preço médio atualizado!');
  };

  const handleSell = ({ amount, accountId, soldQty }) => {
    const inv = sellInv;
    const isFullSale = !inv.quantity || soldQty == null || soldQty >= inv.quantity;
    if (isFullSale) {
      dispatch({ type: 'DELETE_INVESTMENT', payload: inv.id });
    } else {
      const frac = soldQty / inv.quantity;
      dispatch({
        type: 'UPDATE_INVESTMENT',
        payload: {
          ...inv,
          quantity: round5(inv.quantity - soldQty),
          invested: Math.round(inv.invested * (1 - frac) * 100) / 100,
          current: Math.round(inv.current * (1 - frac) * 100) / 100,
        },
      });
    }
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: uuidv4(),
        description: `Venda/Resgate — ${inv.name}`,
        value: amount,
        currency: txCurrencyOf(accountId),
        type: 'income',
        accountId,
        date: new Date().toISOString().slice(0, 10),
        status: 'paid',
        categoryId: 'inv',
        recurrent: false,
        notes: 'Lançado automaticamente ao vender/resgatar investimento',
      },
    });
    toast.success('Venda/resgate lançado com sucesso!');
  };

  const handleDividend = ({ amount, tax, accountId, date, tipo }) => {
    const inv = dividendInv;
    const taxVal = tax || 0;
    // No total do ativo entra o LÍQUIDO (bruto − imposto retido) — é o que a
    // corretora mostra e o que de fato ficou na conta
    const liquidInv = amountInInvCurrency(amount - taxVal, accountId, inv);
    dispatch({
      type: 'UPDATE_INVESTMENT',
      payload: {
        ...inv,
        dividendsReceived: Math.round(((inv.dividendsReceived || 0) + liquidInv) * 100) / 100,
      },
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: uuidv4(),
        description: `${tipo} — ${inv.name}`,
        value: amount,
        currency: txCurrencyOf(accountId),
        type: 'income',
        accountId,
        date,
        status: 'paid',
        categoryId: 'inv',
        recurrent: false,
        notes: `${tipo} lançado automaticamente pelo investimento`,
      },
    });
    // Imposto retido na fonte: sai da mesma conta na sequência — o saldo fica
    // com o líquido e o extrato do ativo mostra as duas pernas
    if (taxVal > 0) {
      dispatch({
        type: 'ADD_TRANSACTION',
        payload: {
          id: uuidv4(),
          description: `Imposto retido — ${inv.name}`,
          value: taxVal,
          currency: txCurrencyOf(accountId),
          type: 'expense',
          accountId,
          date,
          status: 'paid',
          categoryId: 'contas',
          subcategory: 'Imposto/Taxa',
          recurrent: false,
          notes: `Imposto retido na fonte sobre ${tipo.toLowerCase()}`,
        },
      });
    }
    toast.success(`${tipo} lançado com sucesso!`);
  };

  const openEdit = (inv) => { setEditInv(inv); setModal(true); };
  const openNew = () => { setEditInv(null); setModal(true); };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // permite selecionar o mesmo arquivo de novo depois
    if (!file) return;
    setImporting(true);
    try {
      const base64 = await fileToBase64(file);
      const rows = await parseProventosPdf(base64);
      if (!rows.length) { toast.error('Não encontrei nenhuma linha nesse PDF.'); return; }
      setImportRows(rows);
    } catch (err) {
      toast.error(err.message || 'Erro ao ler o PDF.');
    } finally {
      setImporting(false);
    }
  };

  const handleImportConfirm = (selectedRows) => {
    // Agrupa por investimento (o mesmo ativo pode ter várias datas de provento)
    const byInvId = {};
    selectedRows.forEach(r => {
      if (!byInvId[r.inv.id]) byInvId[r.inv.id] = { inv: r.inv, novos: [] };
      byInvId[r.inv.id].novos.push({ id: uuidv4(), valorLiquido: r.valorLiquido, data: r.data, tipo: r.tipo });
    });
    Object.values(byInvId).forEach(({ inv, novos }) => {
      dispatch({
        type: 'UPDATE_INVESTMENT',
        payload: { ...inv, proventosPrevistos: [...(inv.proventosPrevistos || []), ...novos] },
      });
    });
    toast.success(`${selectedRows.length} provento(s) previsto(s) importado(s)!`);
  };

  return (
    <div className="page-enter">
      {/* Header */}
      <div className="row" style={{ marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Investimentos</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>Acompanhe sua carteira</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleFileSelected} />
          <button className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={importing} title="Importar PDF de Proventos Previstos da corretora — só entram os FII">
            {importing ? <><i className="fas fa-circle-notch fa-spin"></i> Lendo PDF...</> : <><i className="fas fa-file-import"></i> Importar Proventos</>}
          </button>
          <button className="btn btn-primary" onClick={openNew}>
            <i className="fas fa-plus"></i> Novo
          </button>
        </div>
      </div>

      {/* Metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="metric-label" style={{ marginBottom: 6 }}>Total Investido</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{formatCurrency(totalInvested)}</div>
        </div>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="metric-label" style={{ marginBottom: 6 }}>Valor Atual</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--blue)' }}>{formatCurrency(totalCurrent)}</div>
        </div>
        <div className="card" style={{ marginBottom: 0, background: totalGain >= 0 ? '#f0fdf4' : '#fff1f1', borderColor: totalGain >= 0 ? '#bbf7d0' : '#fecaca' }}>
          <div className="metric-label" style={{ marginBottom: 6 }}>Rendimento</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: totalGain >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain)}
          </div>
          <div style={{ fontSize: 12, color: totalGain >= 0 ? 'var(--green)' : 'var(--red)', marginTop: 2 }}>
            {totalGain >= 0 ? '+' : ''}{gainPct}%
          </div>
        </div>
      </div>

      {/* Gráficos: evolução do patrimônio + proventos mensais */}
      <PortfolioEvolutionChart />
      <DividendsChart />

      {/* Metas: alocação alvo (rebalanceamento) + renda passiva */}
      <AllocationCard />
      <PassiveIncomeCard />

      {/* Distribuição da Carteira — linhas clicáveis filtram a lista abaixo */}
      {investments.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div className="section-title" style={{ margin: 0 }}>Distribuição da Carteira</div>
            {activeType !== 'Todos' && (
              <button
                onClick={() => setActiveType('Todos')}
                style={{ fontSize: 11, color: 'var(--text2)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 99, padding: '3px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <i className="fas fa-xmark" style={{ fontSize: 10 }}></i> Ver todos
              </button>
            )}
          </div>
          {Object.entries(byType).map(([type, data]) => {
            const pct = totalCurrent > 0 ? Math.round(data.current / totalCurrent * 100) : 0;
            const color = TYPE_COLORS[type] || '#888';
            const isActive = activeType === type;
            return (
              <div
                key={type}
                onClick={() => setActiveType(isActive ? 'Todos' : type)}
                style={{
                  marginBottom: 10,
                  padding: '8px 10px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  background: isActive ? `${color}14` : 'transparent',
                  border: isActive ? `1px solid ${color}40` : '1px solid transparent',
                  transition: 'background 0.15s, border-color 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }}></span>
                    <span style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? color : 'var(--text)' }}>{type}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {formatCurrency(data.current)} <span style={{ color: isActive ? color : 'var(--text2)', fontWeight: 600 }}>({pct}%)</span>
                    </span>
                    <i className="fas fa-chevron-right" style={{ fontSize: 10, color: isActive ? color : 'var(--border)', transition: 'color 0.15s' }}></i>
                  </div>
                </div>
                <div className="progress-bar" style={{ height: 6 }}>
                  <div className="progress-fill" style={{ width: `${pct}%`, background: color, opacity: isActive ? 1 : 0.7 }}></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Proventos Previstos — todos os ativos juntos, ordenados por data */}
      {allProventos.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span><i className="fas fa-calendar-check" style={{ marginRight: 8, color: 'var(--blue)' }}></i>Proventos Previstos</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--green)' }}>{formatCurrency(totalProventosPrevistos)}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {allProventos.map(p => (
              <div key={p.id} className="tx-item" style={{ cursor: 'pointer' }} onClick={() => setConfirmProvento({ investment: p.investment, provento: p })}>
                <div className="tx-info">
                  <div className="tx-desc">{p.investment.name}</div>
                  <div className="tx-cat">{p.tipo} · {new Date(p.data + 'T00:00:00').toLocaleDateString('pt-BR')}</div>
                </div>
                <div className="tx-right" style={{ fontSize: 14, fontWeight: 600, color: 'var(--green)' }}>
                  {formatCurrency(p.valorLiquido)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtro por tipo — integrado na Distribuição da Carteira acima */}

      {/* Investment items */}
      <div className="card">
        {visibleInvestments.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-chart-line"></i>
            <div>{investments.length === 0 ? 'Nenhum investimento cadastrado' : `Nenhum ativo em "${activeType}"`}</div>
            <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={openNew}>
              <i className="fas fa-plus"></i> Adicionar
            </button>
          </div>
        ) : visibleInvestments.map(inv => {
          const gain = inv.current - inv.invested;
          const gainPctItem = inv.invested > 0 ? (gain / inv.invested * 100).toFixed(2) : '0.00';
          const color = TYPE_COLORS[inv.type] || '#888';
          const initial = inv.name.charAt(0).toUpperCase();
          // Bola de neve: dividendos acumulados já dão pra comprar mais cota(s) inteira(s)
          const precoCota = inv.quantity > 0 ? inv.current / inv.quantity : 0;
          const cotasExtras = precoCota > 0 && inv.dividendsReceived > 0 ? Math.floor(inv.dividendsReceived / precoCota) : 0;
          const proventos = (inv.proventosPrevistos || []).slice().sort((a, b) => a.data.localeCompare(b.data));
          const proximoProvento = proventos[0];

          return (
            <div key={inv.id} className="tx-item" style={{ cursor: 'pointer', gap: 12 }} onClick={() => setDetailInv(inv)}>
              <div style={{
                width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                background: color, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16
              }}>
                {initial}
              </div>
              <div className="tx-info">
                <div className="tx-desc">{inv.name}</div>
                <div className="tx-cat">
                  {inv.currency === 'USD' && '🇺🇸 '}{inv.type}{inv.institution ? ` • ${inv.institution}` : ''}{inv.quantity ? ` • ${inv.quantity} cotas • PM ${inv.currency === 'USD' ? 'US$' : 'R$'} ${(inv.invested / inv.quantity).toFixed(2)}` : ''}
                </div>
                {!!inv.dividendsReceived && (
                  <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 2 }}>
                    <i className="fas fa-coins" style={{ marginRight: 4 }}></i>
                    {formatMoney(inv.dividendsReceived, inv.currency)} em dividendos/JCP
                  </div>
                )}
                {cotasExtras >= 1 && (
                  <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 2, fontWeight: 600 }}>
                    🐹 Bola de neve: já dá pra comprar {cotasExtras} cota{cotasExtras !== 1 ? 's' : ''} a mais!
                  </div>
                )}
                {proximoProvento && (
                  <div
                    style={{ fontSize: 11, color: 'var(--blue)', marginTop: 2, cursor: 'pointer' }}
                    onClick={e => { e.stopPropagation(); setConfirmProvento({ investment: inv, provento: proximoProvento }); }}
                  >
                    <i className="fas fa-calendar-check" style={{ marginRight: 4 }}></i>
                    Provento previsto: {formatCurrency(proximoProvento.valorLiquido)} em {new Date(proximoProvento.data + 'T00:00:00').toLocaleDateString('pt-BR')}
                    {proventos.length > 1 && ` (+${proventos.length - 1})`}
                  </div>
                )}
              </div>
              <div className="tx-right">
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', textAlign: 'right' }}>
                  {formatMoney(inv.current, inv.currency)}
                </div>
                {inv.currency === 'USD' && (
                  <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'right', marginTop: 1 }}>
                    ≈ {formatCurrency(toBRL(inv.current, 'USD', state.usdRate))}
                  </div>
                )}
                <div style={{ fontSize: 11, color: gain >= 0 ? 'var(--green)' : 'var(--red)', textAlign: 'right', marginTop: 2 }}>
                  {gain >= 0 ? '+' : ''}{formatMoney(gain, inv.currency)} ({gain >= 0 ? '+' : ''}{gainPctItem}%)
                </div>
              </div>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontSize: 13, padding: '4px 3px', flexShrink: 0 }}
                onClick={e => { e.stopPropagation(); setBuyMoreInv(inv); }}
                title={inv.quantity ? 'Comprar mais' : 'Aportar mais'}
              >
                <i className="fas fa-cart-plus"></i>
              </button>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gold)', fontSize: 13, padding: '4px 3px', flexShrink: 0 }}
                onClick={e => { e.stopPropagation(); setDividendInv(inv); }}
                title="Dividendo / JCP recebido"
              >
                <i className="fas fa-coins"></i>
              </button>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)', fontSize: 13, padding: '4px 3px', flexShrink: 0 }}
                onClick={e => { e.stopPropagation(); setSellInv(inv); }}
                title="Vender / Resgatar"
              >
                <i className="fas fa-sack-dollar"></i>
              </button>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 13, padding: '4px 3px', flexShrink: 0 }}
                onClick={e => { e.stopPropagation(); handleDelete(inv.id); }}
                title="Excluir"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          );
        })}
      </div>

      {modal && (
        <InvestModal
          inv={editInv}
          onClose={() => { setModal(false); setEditInv(null); }}
          onSave={handleSave}
        />
      )}

      {sellInv && (
        <SellModal
          inv={sellInv}
          onClose={() => setSellInv(null)}
          onConfirm={handleSell}
        />
      )}

      {buyMoreInv && (
        <BuyMoreModal
          inv={buyMoreInv}
          onClose={() => setBuyMoreInv(null)}
          onConfirm={handleBuyMore}
        />
      )}

      {dividendInv && (
        <DividendModal
          inv={dividendInv}
          onClose={() => setDividendInv(null)}
          onConfirm={handleDividend}
        />
      )}

      {detailInv && (
        <InvestDetailModal
          inv={detailInv}
          onClose={() => setDetailInv(null)}
          onEdit={(inv) => { setEditInv(inv); setModal(true); }}
          onBuyMore={(inv) => setBuyMoreInv(inv)}
          onSell={(inv) => setSellInv(inv)}
          onDividend={(inv) => setDividendInv(inv)}
          onDelete={(id) => { if (window.confirm('Excluir este investimento?')) dispatch({ type: 'DELETE_INVESTMENT', payload: id }); }}
        />
      )}

      {confirmProvento && (
        <ConfirmProventoModal
          investment={confirmProvento.investment}
          provento={confirmProvento.provento}
          onClose={() => setConfirmProvento(null)}
        />
      )}

      {importRows && (
        <ImportProventosModal
          rows={importRows}
          investments={investments}
          onClose={() => setImportRows(null)}
          onImport={handleImportConfirm}
        />
      )}
    </div>
  );
}
